import React, { useState, useEffect } from 'react';
import { Card, Row, Col, DatePicker, Button, Statistic, Table, Select, Space, message, Divider } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, DollarOutlined, SyncOutlined } from '@ant-design/icons';
import { fluxoCaixaService, unidadeService } from '../services/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const FluxoCaixa = () => {
  const [loading, setLoading] = useState(false);
  const [fluxo, setFluxo] = useState(null);
  const [unidades, setUnidades] = useState([]);
  const [dataInicio, setDataInicio] = useState(dayjs().startOf('year'));
  const [dataFim, setDataFim] = useState(dayjs().endOf('year'));
  const [unidadeSelecionada, setUnidadeSelecionada] = useState(null);

  useEffect(() => {
    fetchUnidades();
    fetchFluxoCaixa();
  }, []);

  const fetchUnidades = async () => {
    try {
      const response = await unidadeService.list();
      setUnidades(response.data);
    } catch (error) {
      console.error('Erro ao carregar unidades');
    }
  };

  const fetchFluxoCaixa = async () => {
    setLoading(true);
    try {
      const params = {
        data_inicio: dataInicio.format('YYYY-MM-DD'),
        data_fim: dataFim.format('YYYY-MM-DD'),
      };
      if (unidadeSelecionada) {
        params.id_unidade = unidadeSelecionada;
      }

      const response = await fluxoCaixaService.get(params);
      setFluxo(response.data);
    } catch (error) {
      message.error('Erro ao carregar fluxo de caixa');
      console.error(error);
    }
    setLoading(false);
  };

  const handlePeriodoChange = (dates) => {
    if (dates) {
      setDataInicio(dates[0]);
      setDataFim(dates[1]);
    }
  };

  const handleConsultar = () => {
    fetchFluxoCaixa();
  };

  const columnsFluxoMensal = [
    {
      title: 'Mês/Ano',
      dataIndex: 'mes',
      key: 'mes',
      render: (mes) => dayjs(mes + '-01').format('MMMM/YYYY'),
    },
    {
      title: 'Receitas Previstas',
      dataIndex: 'receitas_previstas',
      key: 'receitas_previstas',
      render: (valor) => `R$ ${valor?.toFixed(2)}`,
    },
    {
      title: 'Receitas Recebidas',
      dataIndex: 'receitas_recebidas',
      key: 'receitas_recebidas',
      render: (valor) => `R$ ${valor?.toFixed(2)}`,
    },
    {
      title: 'Despesas Previstas',
      dataIndex: 'despesas_previstas',
      key: 'despesas_previstas',
      render: (valor) => `R$ ${valor?.toFixed(2)}`,
    },
    {
      title: 'Despesas Pagas',
      dataIndex: 'despesas_pagas',
      key: 'despesas_pagas',
      render: (valor) => `R$ ${valor?.toFixed(2)}`,
    },
    {
      title: 'Folha Pagto Prevista',
      dataIndex: 'despesas_folha_previstas',
      key: 'despesas_folha_previstas',
      render: (valor) => (
        <span style={{ color: '#722ed1', fontWeight: 'bold' }}>
          R$ {(valor || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: 'Folha Pagto Paga',
      dataIndex: 'despesas_folha_pagas',
      key: 'despesas_folha_pagas',
      render: (valor) => (
        <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
          R$ {(valor || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: 'Saldo Previsto',
      dataIndex: 'saldo_previsto',
      key: 'saldo_previsto',
      render: (valor) => (
        <span style={{ color: valor >= 0 ? 'green' : 'red', fontWeight: 'bold' }}>
          R$ {valor?.toFixed(2)}
        </span>
      ),
    },
    {
      title: 'Saldo Realizado',
      dataIndex: 'saldo_realizado',
      key: 'saldo_realizado',
      render: (valor) => (
        <span style={{ color: valor >= 0 ? 'green' : 'red', fontWeight: 'bold' }}>
          R$ {valor?.toFixed(2)}
        </span>
      ),
    },
  ];

  return (
    <div>
      <Card title="Fluxo de Caixa" extra={
        <Space>
          <RangePicker
            value={[dataInicio, dataFim]}
            onChange={handlePeriodoChange}
            format="DD/MM/YYYY"
          />
          <Select
            placeholder="Todas as unidades"
            style={{ width: 200 }}
            allowClear
            value={unidadeSelecionada}
            onChange={setUnidadeSelecionada}
          >
            {unidades.map((unidade) => (
              <Select.Option key={unidade.id} value={unidade.id}>
                {unidade.nome_fantasia}
              </Select.Option>
            ))}
          </Select>
          <Button
            type="primary"
            icon={<SyncOutlined />}
            onClick={handleConsultar}
            loading={loading}
          >
            Consultar
          </Button>
        </Space>
      }>
        {fluxo && (
          <>
            <Divider>Resumo do Período</Divider>
            
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Receitas Previstas"
                    value={fluxo.resumo.receitas.previstas}
                    precision={2}
                    prefix="R$"
                    valueStyle={{ color: '#3f8600' }}
                    suffix={
                      <span style={{ fontSize: 14, color: '#888' }}>
                        ({fluxo.resumo.receitas.quantidade_total} parcelas)
                      </span>
                    }
                  />
                </Card>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Receitas Recebidas"
                    value={fluxo.resumo.receitas.recebidas}
                    precision={2}
                    prefix="R$"
                    valueStyle={{ color: '#52c41a' }}
                    suffix={
                      <ArrowUpOutlined />
                    }
                  />
                  <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
                    {fluxo.resumo.receitas.quantidade_recebidas} de {fluxo.resumo.receitas.quantidade_total} parcelas recebidas
                  </div>
                </Card>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Receitas Pendentes"
                    value={fluxo.resumo.receitas.pendentes}
                    precision={2}
                    prefix="R$"
                    valueStyle={{ color: '#faad14' }}
                  />
                  <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
                    {fluxo.resumo.receitas.quantidade_pendentes} parcelas pendentes
                  </div>
                </Card>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Despesas Previstas"
                    value={fluxo.resumo.despesas.previstas}
                    precision={2}
                    prefix="R$"
                    valueStyle={{ color: '#cf1322' }}
                    suffix={
                      <span style={{ fontSize: 14, color: '#888' }}>
                        ({fluxo.resumo.despesas.quantidade_total} despesas)
                      </span>
                    }
                  />
                </Card>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Despesas Pagas"
                    value={fluxo.resumo.despesas.pagas}
                    precision={2}
                    prefix="R$"
                    valueStyle={{ color: '#f5222d' }}
                    suffix={
                      <ArrowDownOutlined />
                    }
                  />
                  <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
                    {fluxo.resumo.despesas.quantidade_pagas} de {fluxo.resumo.despesas.quantidade_total} despesas pagas
                  </div>
                </Card>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Despesas Pendentes"
                    value={fluxo.resumo.despesas.pendentes}
                    precision={2}
                    prefix="R$"
                    valueStyle={{ color: '#fa8c16' }}
                  />
                  <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
                    {fluxo.resumo.despesas.quantidade_pendentes} despesas pendentes
                  </div>
                </Card>
              </Col>
            </Row>

            {fluxo.resumo.despesas.folha_pagamento && (
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col xs={24}>
                  <Card style={{ background: '#f0f5ff', borderColor: '#722ed1' }}>
                    <Row gutter={16}>
                      <Col xs={24} sm={8}>
                        <Statistic
                          title="Pagamentos - Previsto"
                          value={fluxo.resumo.despesas.folha_pagamento.previstas}
                          precision={2}
                          prefix="R$"
                          valueStyle={{ color: '#722ed1' }}
                        />
                      </Col>
                      <Col xs={24} sm={8}>
                        <Statistic
                          title="Pagamentos - Pago"
                          value={fluxo.resumo.despesas.folha_pagamento.pagas}
                          precision={2}
                          prefix="R$"
                          valueStyle={{ color: '#52c41a' }}
                        />
                      </Col>
                      <Col xs={24} sm={8}>
                        <Statistic
                          title="Pagamentos - Pendente"
                          value={fluxo.resumo.despesas.folha_pagamento.pendentes}
                          precision={2}
                          prefix="R$"
                          valueStyle={{ color: '#fa8c16' }}
                        />
                      </Col>
                    </Row>
                    <div style={{ marginTop: 12, color: '#722ed1', fontSize: 12, textAlign: 'center' }}>
                      💵 {fluxo.resumo.despesas.folha_pagamento.quantidade_total} professor(es) com pagamento no período
                    </div>
                  </Card>
                </Col>
              </Row>
            )}

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col xs={24} sm={8}>
                <Card>
                  <Statistic
                    title="Saldo Previsto"
                    value={fluxo.resumo.saldo.previsto}
                    precision={2}
                    prefix="R$"
                    valueStyle={{ 
                      color: fluxo.resumo.saldo.previsto >= 0 ? '#3f8600' : '#cf1322',
                      fontWeight: 'bold'
                    }}
                    prefix={<DollarOutlined />}
                  />
                </Card>
              </Col>

              <Col xs={24} sm={8}>
                <Card>
                  <Statistic
                    title="Saldo Realizado"
                    value={fluxo.resumo.saldo.realizado}
                    precision={2}
                    prefix="R$"
                    valueStyle={{ 
                      color: fluxo.resumo.saldo.realizado >= 0 ? '#52c41a' : '#f5222d',
                      fontWeight: 'bold'
                    }}
                    prefix={<DollarOutlined />}
                  />
                </Card>
              </Col>

              <Col xs={24} sm={8}>
                <Card>
                  <Statistic
                    title="Saldo Pendente"
                    value={fluxo.resumo.saldo.pendente}
                    precision={2}
                    prefix="R$"
                    valueStyle={{ 
                      color: fluxo.resumo.saldo.pendente >= 0 ? '#faad14' : '#fa8c16',
                      fontWeight: 'bold'
                    }}
                    prefix={<DollarOutlined />}
                  />
                </Card>
              </Col>
            </Row>

            <Divider>Fluxo Mensal</Divider>

            <Table
              columns={columnsFluxoMensal}
              dataSource={fluxo.fluxo_mensal}
              rowKey="mes"
              pagination={false}
              loading={loading}
              scroll={{ x: 1000 }}
            />
          </>
        )}

        {!fluxo && !loading && (
          <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>
            Selecione um período e clique em "Consultar" para visualizar o fluxo de caixa
          </div>
        )}
      </Card>
    </div>
  );
};

export default FluxoCaixa;
